import React from "react";
import {
  Box,
  Typography,
  useMediaQuery,
  useTheme,
  Paper,
  alpha,
} from "@mui/material";
import { motion } from "framer-motion";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  CartesianGrid,
  LabelList,
} from "recharts";

const COLORS = {
  strength:   "#e07b39",
  attendance: "#330066",
};

// ─── Tooltip ──────────────────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;

  const strength   = payload.find((p) => p.dataKey === "strength")?.value   || 0;
  const attendance = payload.find((p) => p.dataKey === "attendance")?.value || 0;
  const percentage = strength > 0 ? ((attendance / strength) * 100).toFixed(1) : 0;

  return (
    <Paper
      elevation={4}
      sx={{
        backgroundColor: "#fff",
        borderRadius: "12px",
        padding: "12px 16px",
        border: "1px solid #D0D8EC",
        minWidth: "200px",
      }}
    >
      <Typography sx={{ fontWeight: 700, fontSize: 13, color: "#330066", mb: 1, borderBottom: "1px solid #D0D8EC", pb: 0.8 }}>
        {label}
      </Typography>
      {[
        { label: "Actual Strength", value: strength,   color: COLORS.strength   },
        { label: "Attendance",      value: attendance, color: COLORS.attendance },
      ].map((row) => (
        <Box key={row.label} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.6 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
            <Box sx={{ width: 9, height: 9, borderRadius: "3px", bgcolor: row.color }} />
            <Typography sx={{ fontSize: 12, color: "#334155" }}>{row.label}:</Typography>
          </Box>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#330066" }}>
            {Number(row.value).toLocaleString()}
          </Typography>
        </Box>
      ))}
      <Box sx={{ mt: 1, pt: 0.5, borderTop: "1px solid #D0D8EC" }}>
        <Typography sx={{ fontSize: 11, color: "#330066", fontWeight: 600 }}>
          Attendance Rate: <strong>{percentage}%</strong>
        </Typography>
      </Box>
    </Paper>
  );
};

// ─── Legend ───────────────────────────────────────────────────────────────────
const CustomLegend = () => (
  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2.5, mt: 2, justifyContent: "center" }}>
    {[
      { color: COLORS.strength,   label: "Actual Strength" },
      { color: COLORS.attendance, label: "Attendance"      },
    ].map((item) => (
      <Box key={item.label} sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
        <Box sx={{ width: 12, height: 12, borderRadius: "3px", bgcolor: item.color }} />
        <Typography sx={{ fontSize: 12, color: "#475569" }}>{item.label}</Typography>
      </Box>
    ))}
  </Box>
);

// ─── Inside-bar label ─────────────────────────────────────────────────────────
const InsideLabel = (props) => {
  const { x, y, width, height, value } = props;
  if (!value || width < 30) return null;
  return (
    <text
      x={x + width / 2}
      y={y + height / 2}
      textAnchor="middle"
      dominantBaseline="central"
      fill="#fff"
      fontSize={11}
      fontWeight={600}
    >
      {Number(value).toLocaleString()}
    </text>
  );
};

// ─── Main ─────────────────────────────────────────────────────────────────────
export function EmployeeStrengthAttendanceChart({ allAttendance = [] }) {
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const chartData = (allAttendance || [])
    .filter((item) => {
      const loc = item?.loc || "";
      return loc && loc !== "Total";
    })
    .map((item) => {
      const strength   = parseInt(item?.str || 0);
      const attendance = parseInt(item?.att || 0);
      const loc        = item?.loc || "";
      const typeMap    = { E: "Executive", S: "Supervisory", C: "Clerical", I: "Industrial", T: "Trainee" };
      return {
        type:       typeMap[loc] || loc,
        strength,
        attendance,
        percentage: parseInt(item?.pre || 0),
      };
    });

  const totals = chartData.reduce(
    (acc, d) => ({
      strength:   acc.strength   + (d.strength   || 0),
      attendance: acc.attendance + (d.attendance || 0),
    }),
    { strength: 0, attendance: 0 }
  );

  const totalPct = totals.strength > 0
    ? ((totals.attendance / totals.strength) * 100).toFixed(1)
    : 0;

  const barSize  = isMobile ? 16 : 20;
  const chartH   = chartData.length * (barSize * 2 + 16) + 60;
  const YAXIS_W  = isMobile ? 85 : 105;

  if (!chartData.length) {
    return (
      <Paper elevation={0} sx={{ backgroundColor: "#F4F6FA", borderRadius: "20px", padding: "24px", border: "1px solid #D0D8EC", textAlign: "center" }}>
        <Typography sx={{ color: "#64748B" }}>No attendance data available</Typography>
      </Paper>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, type: "spring", stiffness: 100 }}
    >
      <Paper
        elevation={0}
        sx={{
          overflow: "hidden",
          backgroundColor: "#F4F6FA",
          borderRadius: "20px",
          padding: { xs: "16px", sm: "20px", md: "24px" },
          border: "1px solid #D0D8EC",
          boxShadow: "0 4px 24px rgba(37,99,235,0.07)",
        }}
      >
        {/* ── Header ── */}
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: { xs: "17px", sm: "19px", md: "21px" }, fontWeight: 700, color: "#330066", letterSpacing: "-0.01em", mb: 0.5 }}>
            Employee Strength & Attendance Overview
          </Typography>
          <Typography sx={{ fontSize: 13, color: "#64748b" }}>
            Stacked view — actual strength vs attendance per employee type
          </Typography>
        </Box>

        {/* ── Chart ── */}
        <Box sx={{ width: "100%", height: chartH, minHeight: 240 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              layout="vertical"
              margin={{ top: 4, right: isMobile ? 50 : 65, left: 0, bottom: 8 }}
              barSize={barSize}
              barCategoryGap="30%"
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#D0D8EC" horizontal={false} vertical={true} />

              <YAxis
                type="category"
                dataKey="type"
                axisLine={false}
                tickLine={false}
                tick={{ fill: "#330066", fontSize: isMobile ? 12 : 13, fontWeight: 600 }}
                width={YAXIS_W}
                interval={0}
              />

              <XAxis
                type="number"
                axisLine={false}
                tickLine={false}
                tick={{ fill: "#94a3b8", fontSize: isMobile ? 10 : 11 }}
                tickFormatter={(v) => v.toLocaleString()}
              />

              <RechartsTooltip
                content={<CustomTooltip />}
                cursor={{ fill: alpha("#0b0c0c", 0.04) }}
              />

              {/* Strength — base segment */}
              <Bar dataKey="strength" name="Actual Strength" stackId="a" fill={COLORS.strength} radius={[0, 0, 0, 0]}>
                <LabelList content={<InsideLabel />} />
              </Bar>

              {/* Attendance — stacked on top of strength */}
              <Bar dataKey="attendance" name="Attendance" stackId="a" fill={COLORS.attendance} radius={[0, 6, 6, 0]}>
                <LabelList content={<InsideLabel />} />
                {/* Percentage label at far right */}
                <LabelList
                  dataKey="percentage"
                  position="right"
                  formatter={(v) => `${v}%`}
                  style={{ fill: "#330066", fontSize: isMobile ? 10 : 11, fontWeight: 700 }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>

        {/* ── Legend ── */}
        <CustomLegend />

        {/* ── Footer totals ── */}
        {/* <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: { xs: 1.5, sm: 3 },
            mt: 3, pt: 2,
            borderTop: "1px solid #D0D8EC",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {[
            { color: COLORS.strength,   label: "Total Strength",   value: totals.strength,   isText: false },
            { color: COLORS.attendance, label: "Total Attendance",  value: totals.attendance, isText: false },
            { color: "#dd1b1b",         label: "Overall Rate",      value: `${totalPct}%`,    isText: true  },
          ].map((item) => (
            <Box key={item.label} sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
              <Box sx={{ width: 9, height: 9, borderRadius: "3px", bgcolor: item.color }} />
              <Typography variant="caption" sx={{ color: "#475569" }}>
                {item.label}:{" "}
                <strong style={{ color: "#330066" }}>
                  {item.isText ? item.value : Number(item.value).toLocaleString()}
                </strong>
              </Typography>
            </Box>
          ))}
        </Box> */}
      </Paper>
    </motion.div>
  );
}